/**
 * Crear subasta (3 pasos): juego → tipo → datos. Toda subasta sale a `pending_review`.
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { useAuth } from "@/lib/auth";
import { usePublicConfig } from "@/lib/publicConfig";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { GameIcon } from "@/components/GameIcon";
import { MultiPhotoInput } from "@/components/MultiPhotoInput";
import { useLeaveGuard } from "@/lib/useLeaveGuard";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { HelpTip, FieldLabel, FieldError, Section, StepBar, inputCls, triggerCls } from "@/components/auction/CrearParts";
import { useAuctionTerms, TermsCheck, TermsAccepted, FEE_NON_REFUNDABLE } from "@/components/auction/AuctionTerms";
import { Clock, Info, Zap, DollarSign, ShieldCheck, Gavel, ArrowLeft, ArrowRight, Check, Sparkles, Layers, Package, Globe } from "lucide-react";
import { HitButton, useAsyncAction, useHeavyFlow } from "@/components/loading";
import { CONDITIONS, condLabel } from "@/lib/conditions";

const DEFAULT_DAY_PRICES = { 1: 0, 3: 2, 5: 4, 7: 6 };
const MIN_PHOTOS = 2;
const MAX_PHOTOS = 12;

const GAMES = [
  { key: "pokemon", label: "Pokémon", tagline: "TCG original" },
  { key: "yugioh", label: "Yu-Gi-Oh!", tagline: "OCG / TCG" },
  { key: "riftbound", label: "Riftbound", tagline: "League of Legends" },
  { key: "other", label: "Otros", tagline: "One Piece, Digimon, Magic, Lorcana…" },
];

// Mantener en sincronía con backend `_AUCTION_OTHER_GAMES`.
const OTHER_GAMES = [
  { key: "one_piece", label: "One Piece" },
  { key: "digimon", label: "Digimon" },
  { key: "dragon_ball_super", label: "Dragon Ball Super" },
  { key: "magic", label: "Magic: The Gathering" },
  { key: "lorcana", label: "Disney Lorcana" },
  { key: "flesh_and_blood", label: "Flesh and Blood" },
  { key: "weiss_schwarz", label: "Weiss Schwarz" },
  { key: "union_arena", label: "Union Arena" },
  { key: "star_wars_unlimited", label: "Star Wars Unlimited" },
  { key: "metazoo", label: "MetaZoo" },
  { key: "cardfight_vanguard", label: "Cardfight!! Vanguard" },
  { key: "altered_tcg", label: "Altered" },
  { key: "sorcery_tcg", label: "Sorcery" },
  { key: "other_custom", label: "Otro (lo describo en el nombre)" },
];

const PRODUCT_TYPES = [
  { key: "card_single", label: "Carta individual", tagline: "Una sola carta", icon: Sparkles },
  { key: "card_group", label: "Grupo de cartas", tagline: "Lote / colección / playset", icon: Layers },
  { key: "sealed", label: "Sellado", tagline: "Booster, caja, ETB, deck, bundle", icon: Package },
];

const CARD_CONDITIONS = CONDITIONS.map((c) => ({ key: c.value, label: condLabel(c.value) }));
const SEALED_CONDITIONS = [
  { key: "SEALED", label: "Sellado de fábrica" },
  { key: "OPEN", label: "Abierto / vuelto a sellar" },
  { key: "DAMAGED", label: "Caja dañada" },
];

const LANGUAGES = [
  { key: "es", label: "Español" }, { key: "en", label: "Inglés" }, { key: "jp", label: "Japonés" },
  { key: "fr", label: "Francés" }, { key: "de", label: "Alemán" }, { key: "it", label: "Italiano" },
  { key: "pt", label: "Portugués" }, { key: "ko", label: "Coreano" }, { key: "zh", label: "Chino" }, { key: "other", label: "Otro" },
];

const HOW_IT_WORKS = [
  { icon: Zap, title: "Subasta Turbo GRATIS", body: "1 día sin pagar NADA. Solo los días extra (3/5/7) tienen un valor pequeño." },
  { icon: DollarSign, title: "0% comisión", body: "Sin comisiones para HitTCG en ninguna subasta. Lo que ofertan es lo que recibes." },
  { icon: Clock, title: "Anti-sniping", body: "Si pujan en los últimos 2 min, la subasta extiende 2 min. Nadie gana por trampa." },
  { icon: ShieldCheck, title: "Solo verificados", body: "Comprar y vender exige verificación de cédula. Ganar y no pagar = cuenta baneada." },
];

async function uploadPublic(file, onProgress) {
  const { file: toUpload } = await compressImage(file);
  const fd = new FormData();
  fd.append("file", toUpload);
  fd.append("scope", "public");
  fd.append("kind", "card");
  const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000, onUploadProgress: (e) => onProgress?.(e.total ? e.loaded / e.total : 0) });
  return r.data.path;
}

const nativeSelect = (bad) => `${inputCls(bad)} appearance-none bg-[#161616] disabled:opacity-50`;

export default function SubastasCrear() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editDraftToken = searchParams.get("draft") || null;
  const resubmitId = searchParams.get("resubmit") || null;
  const terms = useAuctionTerms();
  const [termsOk, setTermsOk] = useState(false);
  const publicConfig = usePublicConfig();
  const dayPrices = publicConfig?.auction_day_prices || DEFAULT_DAY_PRICES;

  const [step, setStep] = useState(1);
  useEffect(() => { window.scrollTo(0, 0); }, [step]);
  const [game, setGame] = useState("");
  const [otherGameKey, setOtherGameKey] = useState("");
  const [productType, setProductType] = useState("");

  const [form, setForm] = useState({
    product_name: "", set_name: "", rarity_text: "", language: "en", condition: "NM", description: "",
    province: "", city: "", shipping_cost_same_province: 3, shipping_cost_other_province: 5,
    accepts_local_delivery: true, meetup_location: "", starting_price: "", duration_days: 1, payment_deadline_days: 1,
  });
  const [photos, setPhotos] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const leave = useLeaveGuard(!!game || !!editDraftToken || !!resubmitId);

  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [shippingConfig, setShippingConfig] = useState(null);

  useEffect(() => {
    api.get("/geo/provinces").then((r) => setProvinces(r.data)).catch(() => {});
    // Prefill rates desde el shipping_config del vendedor si existe.
    api.get("/seller/shipping-config").then((r) => {
      const cfg = r.data;
      if (!cfg) return;
      setShippingConfig(cfg);
      setForm((f) => {
        const next = { ...f };
        if (cfg.mode === "simple") {
          next.shipping_cost_same_province = Number(cfg.same_province_rate ?? f.shipping_cost_same_province);
          next.shipping_cost_other_province = Number(cfg.other_province_rate ?? f.shipping_cost_other_province);
        } else if (cfg.mode === "detailed" && user?.province) {
          const own = cfg.per_province_rates?.[user.province];
          if (own !== undefined) next.shipping_cost_same_province = Number(own);
          const rates = Object.values(cfg.per_province_rates || {}).map(Number).filter((n) => !isNaN(n));
          if (rates.length) {
            rates.sort((a, b) => a - b);
            next.shipping_cost_other_province = Number(rates[Math.floor(rates.length / 2)]);
          }
        } else if (cfg.mode === "meetup_only") {
          next.shipping_cost_same_province = 0;
          next.shipping_cost_other_province = 0;
        }
        return next;
      });
    }).catch(() => {});
  }, [user?.province]);

  useEffect(() => {
    if (!form.province) { setCities([]); return; }
    setLoadingCities(true);
    api.get("/geo/cities", { params: { province: form.province } })
      .then((r) => setCities(r.data))
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [form.province]);

  // Recuperar borrador (?draft=TOKEN) o subasta rechazada para corregir (?resubmit=ID).
  useEffect(() => {
    if (!editDraftToken && !resubmitId) return;
    api.get(editDraftToken ? `/auctions/drafts/${editDraftToken}` : `/auctions/${resubmitId}/resubmit-data`)
      .then((r) => {
        const d = r.data;
        setGame(d.game_slug || "");
        if (d.game_slug === "other") setOtherGameKey(d.other_game_key || "");
        setProductType(d.product_type || "");
        setStep(3);
        setForm((f) => ({
          ...f,
          product_name: d.product_name || "", set_name: d.set_name || "", rarity_text: d.rarity_text || "",
          language: d.language || "en", condition: d.condition || "NM", description: d.description || "",
          province: d.province || "", city: d.city || "",
          shipping_cost_same_province: Number(d.shipping_cost_same_province || 0),
          shipping_cost_other_province: Number(d.shipping_cost_other_province || 0),
          accepts_local_delivery: !!d.accepts_local_delivery, meetup_location: d.meetup_location || "",
          starting_price: String(d.starting_price ?? ""), duration_days: d.duration_days || 1,
          payment_deadline_days: d.payment_deadline_days || 1,
        }));
        setPhotos((d.photos || []).map((p) => ({ _draft_path: p, name: p.split("/").pop() })));
        toast.info(resubmitId ? "Corrige lo que indicó el administrador y reenvía. No pagas de nuevo." : "Borrador recuperado — continúa donde quedaste.");
      })
      .catch(() => toast.error(resubmitId ? "No pudimos cargar la subasta para corregir" : "No pudimos recuperar el borrador"));
    // eslint-disable-next-line
  }, [editDraftToken, resubmitId]);

  useEffect(() => {
    setForm((f) => ({ ...f, condition: productType === "sealed" ? "SEALED" : "NM" }));
  }, [productType]);

  const conditions = productType === "sealed" ? SEALED_CONDITIONS : CARD_CONDITIONS;
  const dayFee = Number(dayPrices[form.duration_days] || 0);
  const upd = (k, v) => setForm((f) => (k === "province" ? { ...f, province: v, city: "" } : { ...f, [k]: v }));

  // Mismas reglas que antes (canSubmit), ahora explicadas campo por campo.
  const errors = useMemo(() => {
    const e = {};
    if (form.product_name.trim().length < 2) e.name = "Escribe el nombre de la carta o producto (mínimo 2 letras)";
    if (photos.length < MIN_PHOTOS) e.photos = `Añade mínimo ${MIN_PHOTOS} fotos — te faltan ${MIN_PHOTOS - photos.length} (recomendado 4+)`;
    if (!form.province) e.province = "Elige tu provincia para calcular el envío";
    if (!form.city) e.city = form.province ? "Elige la ciudad desde donde envías" : "Primero elige provincia";
    if (!(Number(form.shipping_cost_same_province) >= 0)) e.shipSame = "Indica el envío a tu provincia (puede ser 0)";
    if (!(Number(form.shipping_cost_other_province) >= 0)) e.shipOther = "Indica el envío a otras provincias (puede ser 0)";
    if (form.accepts_local_delivery && form.meetup_location.trim().length < 3) e.meetup = "Escribe el punto de encuentro (mínimo 3 letras)";
    if (!(parseFloat(form.starting_price) >= 0)) e.price = "Indica el precio de salida (puede ser 0)";
    else if (!/^\d+$/.test(String(form.starting_price).trim())) e.price = "El precio de salida debe ser un número entero, sin centavos";
    return e;
  }, [form, photos.length]);
  const MISSING = [["name", "nombre", "fld-name"], ["photos", "fotos", "fld-photos"], ["province", "provincia", "fld-province"], ["city", "ciudad", "fld-city"],
    ["shipSame", "envío", "fld-ship-same"], ["shipOther", "envío otras", "fld-ship-other"], ["meetup", "punto de encuentro", "fld-meetup"], ["price", "precio", "fld-price"]]
    .filter(([k]) => errors[k]);

  const canSubmit = useMemo(() => {
    const otherOk = game !== "other" || !!otherGameKey;
    return game && otherOk && productType && form.condition && form.language && MISSING.length === 0 && (terms.accepted || termsOk) &&
      [1, 3, 5, 7].includes(Number(form.duration_days)) && [1, 2].includes(Number(form.payment_deadline_days));
  }, [game, otherGameKey, productType, form, MISSING.length, terms.accepted, termsOk]);

  const goFirstMissing = () => {
    setShowErrors(true);
    const el = MISSING[0] && document.getElementById(MISSING[0][2]);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const f = el.matches("input,select,textarea") ? el : el.querySelector("input,select,textarea,button");
      setTimeout(() => f?.focus({ preventScroll: true }), 350);
    }
  };
  const err = (k) => (showErrors || (k === "price" && /[.,]/.test(String(form.starting_price))) ? errors[k] : null);

  const submit = () => {
    if (!canSubmit) {
      toast.error(!(terms.accepted || termsOk) ? "Debes aceptar los Términos y Condiciones de Subastas." : "Completa todos los campos obligatorios y sube al menos 2 fotos.");
      return;
    }
    publish.run();
  };
  const publish = useAsyncAction(async () => {
    try {
      if (!terms.accepted) await terms.accept(resubmitId ? "resubmit" : "create");
      const uploaded = [];
      setFlow({ step: 0, progress: 0 });
      for (let i = 0; i < photos.length; i++) {
        const p = photos[i];
        if (p && p._draft_path) { uploaded.push(p._draft_path); continue; }
        try {
          uploaded.push(await uploadPublic(p, (f) => setFlow({ step: 0, progress: ((i + f) / photos.length) * 0.7 })));
        } catch (e) {
          console.error("photo upload failed", e);
          toast.error(`No se pudo subir la foto ${i + 1}. Intenta de nuevo.`);
          return false;
        }
      }
      setFlow({ step: 1, progress: 0.75 });
      const body = {
        game, other_game_key: game === "other" ? otherGameKey : null, product_type: productType,
        product_name: form.product_name.trim(), set_name: form.set_name.trim() || null, rarity_text: form.rarity_text.trim() || null,
        language: form.language, condition: form.condition, description: form.description.trim() || null, photos: uploaded,
        province: form.province, city: form.city,
        shipping_cost_same_province: Number(form.shipping_cost_same_province || 0),
        shipping_cost_other_province: Number(form.shipping_cost_other_province || 0),
        accepts_local_delivery: !!form.accepts_local_delivery,
        meetup_location: form.accepts_local_delivery ? form.meetup_location.trim() : null,
        starting_price: parseFloat(form.starting_price), duration_days: Number(form.duration_days),
        payment_deadline_days: Number(form.payment_deadline_days),
      };
      setFlow({ step: 2, progress: 0.85 });
      if (resubmitId) {
        await api.put(`/auctions/${resubmitId}/resubmit`, body);
        leave.allowLeave();
        setFlow({ step: 3, progress: 1 });
        return () => { toast.success("Subasta corregida y reenviada a revisión. No se te cobró nada."); navigate("/subastas/mis-subastas"); };
      }
      const r = editDraftToken ? await api.put(`/auctions/drafts/${editDraftToken}`, body) : await api.post("/auctions", body);
      leave.allowLeave();
      setFlow({ step: 3, progress: 1 });
      return () => {
        if (editDraftToken && r.data.mode === "direct") {
          toast.success("¡Subasta Turbo enviada! Queda en revisión del administrador.");
          navigate("/subastas/mis-subastas");
        } else if (editDraftToken) {
          toast.success("Borrador actualizado — redirigiendo al pago.");
          navigate(`/subastas/pago/${editDraftToken}`);
        } else if (r.data.mode === "pending_payment") {
          toast.info("Para enviar a revisión, primero paga los días adicionales.");
          navigate(`/subastas/pago/${r.data.draft_token}`);
        } else {
          toast.success("¡Subasta enviada! Queda en revisión del administrador. Te avisaremos cuando se apruebe.");
          navigate("/");
        }
      };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando la subasta");
      return false;
    }
  });
  const setFlow = useHeavyFlow(publish.loading, "publish");
  const submitting = publish.loading;

  const selectedGameLabel = game === "other"
    ? (OTHER_GAMES.find((g) => g.key === otherGameKey)?.label || "Otro")
    : (GAMES.find((g) => g.key === game)?.label || "");
  const typeLabel = PRODUCT_TYPES.find((p) => p.key === productType)?.label;

  return (
    <TooltipProvider delayDuration={120}>
      <div className="sx relative overflow-hidden min-h-[80vh]">
        <div className="sx-halo" />
        <div className="relative max-w-3xl mx-auto px-5 pt-7 pb-40">
          <div className="flex items-center justify-between gap-3">
            <div className="sx-mono text-[10px] tracking-[.16em] text-[#ff8a80] flex items-center gap-3"><span className="w-7 h-px bg-[#FF4438]" /> PASO {step} DE 3</div>
            {step === 2 && <button type="button" onClick={() => setStep(1)} data-testid="auctions-back-step1" className="text-sm text-white/65 hover:text-white flex items-center gap-1"><ArrowLeft size={14} /> Cambiar juego</button>}
            {step === 3 && <span className="text-[13px] text-white/55 truncate">{selectedGameLabel} · {typeLabel} · <button type="button" onClick={() => setStep(2)} data-testid="auctions-back-step2" className="text-white hover:underline">Cambiar</button></span>}
          </div>
          {step === 1 && <h1 className="sx-h text-[44px] mt-3">Crear <span className="sx-it">subasta</span></h1>}
          <StepBar step={step} labels={[step > 1 ? selectedGameLabel : "Juego", "Tipo", "Datos"]} />

          {step === 1 && (
            <div className="mt-7 space-y-6">
              <h2 className="sx-h text-[22px]">¿De qué juego es?</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {GAMES.map((g) => {
                  const on = game === g.key;
                  return (
                    <button key={g.key} type="button" data-testid={`auctions-game-${g.key}`}
                            onClick={() => { setGame(g.key); if (g.key !== "other") setOtherGameKey(""); }}
                            className={`relative h-[150px] rounded-[20px] border p-4 flex flex-col justify-between text-left transition-colors ${on ? "border-[#FF4438]/80 bg-gradient-to-b from-[#FF4438]/[.18] to-[#101010]" : "border-white/12 bg-[#101010] hover:border-white/30"}`}>
                      {g.key === "other" ? (
                        <span className="w-12 h-12 rounded-full inline-flex items-center justify-center border border-white/25 bg-white/[.06] text-white/75" data-testid="game-icon-other"><Globe size={22} /></span>
                      ) : <GameIcon game={g.key} size="xl" />}
                      {on && <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#FF4438] text-[#080808] flex items-center justify-center"><Check size={14} strokeWidth={3} /></span>}
                      <span className="flex flex-col">
                        <span className={`text-lg font-extrabold ${on ? "text-white" : "text-white/80"}`}>{g.label}</span>
                        <span className="text-[11px] text-white/45 mt-0.5 leading-snug">{g.tagline}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {game === "other" && (
                <div className="rounded-[20px] border border-[#FF4438]/40 bg-[#FF4438]/[.05] p-4">
                  <FieldLabel required tipText="Selecciona el TCG. Si no aparece en la lista, elige 'Otro' y describe el juego en el nombre del producto." tipTestid="tip-other-game">Elige el juego</FieldLabel>
                  <Select value={otherGameKey} onValueChange={setOtherGameKey}>
                    <SelectTrigger data-testid="auctions-other-game" className={triggerCls}><SelectValue placeholder="Selecciona un juego" /></SelectTrigger>
                    <SelectContent className="bg-[#161616] border-white/15 text-white max-h-[320px]">
                      {OTHER_GAMES.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="rounded-[22px] border border-white/10 bg-[#0d0d0d] p-4">
                <div className="sx-mono text-[10px] tracking-[.14em] text-[#FF4438] flex items-center gap-1.5 mb-3"><Gavel size={12} /> CÓMO FUNCIONAN LAS SUBASTAS EN HITTCG</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {HOW_IT_WORKS.map(({ icon: Icon, title, body }) => (
                    <div key={title} data-testid={`step1-bullet-${title}`} className="rounded-[14px] bg-white/[.03] border border-white/8 p-3">
                      <Icon size={15} className="text-[#FF4438] mb-1.5" />
                      <div className="text-[13px] font-bold">{title}</div>
                      <div className="text-[11px] text-white/50 leading-snug mt-0.5">{body}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-7 space-y-3">
              <h2 className="sx-h text-[32px] mb-5">¿Qué <span className="sx-it">vas a subastar?</span></h2>
              {PRODUCT_TYPES.map(({ key, label, tagline, icon: Icon }) => {
                const on = productType === key;
                return (
                  <button key={key} type="button" data-testid={`auctions-type-${key}`} onClick={() => { setProductType(key); setStep(3); }}
                          className={`w-full rounded-[20px] border p-4 flex items-center gap-4 text-left transition-colors ${on ? "border-[#FF4438]/80 bg-[#FF4438]/[.10]" : "border-white/12 bg-[#101010] hover:border-white/30"}`}>
                    <span data-testid={`auctions-type-icon-${key}`}
                          className={`w-14 h-14 flex-none rounded-[16px] border flex items-center justify-center transition-colors ${on ? "border-[#FF4438]/70 bg-[#FF4438]/[.14] text-[#FF4438] shadow-[0_0_18px_rgba(255,68,56,.3)]" : "border-white/15 bg-white/[.04] text-white/75"}`}>
                      <Icon size={26} strokeWidth={1.8} />
                    </span>
                    <span className="flex-1 min-w-0"><span className="block text-lg font-extrabold">{label}</span><span className="block text-[13px] text-white/55">{tagline}</span></span>
                    <ArrowRight size={18} className={on ? "text-[#FF4438]" : "text-white/40"} />
                  </button>
                );
              })}
            </div>
          )}

          {step === 3 && (
            <div className="mt-6 space-y-4">
              <Section n="01" title="DATOS DEL PRODUCTO" invalid={!!err("name")} testid="auctions-section-product">
                <div id="fld-name">
                  <FieldLabel required tipText="Nombre descriptivo del producto. Ejemplo: 'Charizard Base Set 4/102' o 'Booster Box Scarlet & Violet 151'. Es lo primero que verá el comprador." tipTestid="tip-product-name">Nombre del producto</FieldLabel>
                  <input data-testid="auctions-product-name" value={form.product_name} onChange={(e) => upd("product_name", e.target.value)} maxLength={200}
                         placeholder={productType === "sealed" ? "Booster Box Scarlet & Violet 151" : "Charizard Base Set 4/102"} className={inputCls(err("name"))} />
                  <FieldError msg={err("name")} testid="err-name" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <FieldLabel required tipText="Idioma impreso en la carta o producto. En Pokémon y Yu-Gi-Oh! cambia bastante el precio." tipTestid="tip-language">Idioma</FieldLabel>
                    <Select value={form.language} onValueChange={(v) => upd("language", v)}>
                      <SelectTrigger data-testid="auctions-language" className={triggerCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#161616] border-white/15 text-white">{LANGUAGES.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <FieldLabel required tipText={productType === "sealed" ? "Estado del producto sellado. 'Sellado de fábrica' = nunca abierto, con los precintos originales." : "Condición estándar TCG. NM = casi perfecta, LP = juego ligero, MP = marcas visibles, HP = daño notorio, DMG = dañada."} tipTestid="tip-condition">Condición</FieldLabel>
                    <Select value={form.condition} onValueChange={(v) => upd("condition", v)}>
                      <SelectTrigger data-testid="auctions-condition" className={triggerCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#161616] border-white/15 text-white">{conditions.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className={`grid gap-3 mt-4 ${productType !== "sealed" ? "grid-cols-2" : "grid-cols-1"}`}>
                  <div>
                    <FieldLabel tipText="Set, edición, bloque o colección a la que pertenece. Opcional pero recomendado — ayuda al comprador a identificar exactamente el producto." tipTestid="tip-set-name">Set / Edición</FieldLabel>
                    <input data-testid="auctions-set-name" value={form.set_name} onChange={(e) => upd("set_name", e.target.value)} placeholder="Base Set, SV01, LOB, Origins…" maxLength={200} className={inputCls()} />
                  </div>
                  {productType !== "sealed" && (
                    <div>
                      <FieldLabel tipText="Rareza impresa en la carta (ejemplo: Rare Holo, Secret Rare, Ultra Rare). Si no estás seguro, déjalo vacío y acláralo en la descripción." tipTestid="tip-rarity">Rareza</FieldLabel>
                      <input data-testid="auctions-rarity-text" value={form.rarity_text} onChange={(e) => upd("rarity_text", e.target.value)} placeholder="Rare Holo, Secret Rare…" maxLength={120} className={inputCls()} />
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <FieldLabel tipText="Detalles extra que no caben en los campos anteriores: cantidad exacta del lote, defectos puntuales, procedencia, etc. Máximo 2000 caracteres." tipTestid="tip-description">Descripción</FieldLabel>
                  <Textarea data-testid="auctions-description" value={form.description} onChange={(e) => upd("description", e.target.value)} placeholder="Cuenta lo que haga falta sobre el producto…" maxLength={2000} rows={3}
                            className="sx-input rounded-[14px] bg-[#161616] border-white/14 text-[15px]" />
                </div>
              </Section>

              <Section n="02" title="FOTOS REALES" invalid={!!err("photos")} testid="auctions-section-photos"
                       right={<span className={`sx-mono text-[10px] tracking-[.12em] ${photos.length < MIN_PHOTOS ? "text-[#ff6b61]" : "text-[#34D399]"}`}>{photos.length} / {MIN_PHOTOS} MÍN.</span>}>
                <div id="fld-photos" className="text-[12px] text-white/50 mb-3">Mínimo {MIN_PHOTOS} fotos propias del producto (frente y dorso para cartas, caja + precinto para sellados). Nada de imágenes del catálogo oficial — fotos verdaderas tomadas por ti.</div>
                <MultiPhotoInput photos={photos} onChange={setPhotos} min={MIN_PHOTOS} recommended={4} max={MAX_PHOTOS} testid="auctions-photos" fullPreview />
                <FieldError msg={err("photos")} testid="err-photos" />
              </Section>

              <Section n="03" title="LOGÍSTICA Y ENVÍO" invalid={!!(err("province") || err("city") || err("meetup") || err("shipSame") || err("shipOther"))} testid="auctions-section-logistics">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel required tipText="Provincia desde donde vas a enviar o entregar. El comprador la verá antes de pujar para calcular tiempos y costos." tipTestid="tip-province">Provincia</FieldLabel>
                    <select id="fld-province" data-testid="auctions-province" value={form.province} onChange={(e) => upd("province", e.target.value)} className={nativeSelect(err("province"))}>
                      <option value="">Elige una provincia</option>
                      {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <FieldError msg={err("province")} testid="err-province" />
                  </div>
                  <div>
                    <FieldLabel required tipText="Ciudad desde donde sale el envío. Se habilita después de elegir provincia." tipTestid="tip-city">Ciudad</FieldLabel>
                    <select id="fld-city" data-testid="auctions-city" value={form.city} onChange={(e) => upd("city", e.target.value)} disabled={!form.province || loadingCities} className={nativeSelect(err("city") && form.province)}>
                      <option value="">{!form.province ? "Primero elige provincia" : loadingCities ? "Cargando ciudades…" : `Elige una ciudad (${cities.length})`}</option>
                      {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {form.province && <FieldError msg={err("city")} testid="err-city" />}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <FieldLabel tipText="Costo que cobras al ganador cuando vive en la MISMA provincia que tú. Si solo haces entrega personal, pon 0." tipTestid="tip-ship-same">Misma provincia (USD)</FieldLabel>
                    <input id="fld-ship-same" data-testid="auctions-ship-same" type="number" step="0.5" min="0" value={form.shipping_cost_same_province} onChange={(e) => upd("shipping_cost_same_province", e.target.value)} className={inputCls(err("shipSame"))} />
                    <FieldError msg={err("shipSame")} />
                  </div>
                  <div>
                    <FieldLabel tipText="Costo que cobras al ganador cuando vive en OTRA provincia (Servientrega, Laar, Tramaco, etc.). Suele ser mayor que el interno." tipTestid="tip-ship-other">Otras provincias (USD)</FieldLabel>
                    <input id="fld-ship-other" data-testid="auctions-ship-other" type="number" step="0.5" min="0" value={form.shipping_cost_other_province} onChange={(e) => upd("shipping_cost_other_province", e.target.value)} className={inputCls(err("shipOther"))} />
                    <FieldError msg={err("shipOther")} />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between gap-3 px-4 h-[52px] rounded-[14px] border border-white/12 bg-black/20">
                  <span className="text-sm text-white/85 flex items-center gap-1.5">Acepta entregas personales
                    <HelpTip text="Si puedes quedar con el comprador en un punto de la ciudad para entregar en persona, actívalo. Suele dar más confianza y agilizar la entrega." testid="tip-local-delivery" />
                  </span>
                  <Switch data-testid="auctions-local-delivery" checked={!!form.accepts_local_delivery} onCheckedChange={(v) => upd("accepts_local_delivery", v)} className="data-[state=checked]:bg-[#FF4438]" />
                </div>
                {form.accepts_local_delivery && (
                  <div className="mt-4">
                    <FieldLabel required tipText="Punto de encuentro público propuesto para la entrega en persona. Ejemplo: 'Centro Comercial Quicentro, patio de comidas' o 'Parque La Carolina'. Lo verá solo el ganador." tipTestid="tip-meetup">Punto de encuentro</FieldLabel>
                    <input id="fld-meetup" data-testid="auctions-meetup-location" value={form.meetup_location} onChange={(e) => upd("meetup_location", e.target.value)} placeholder="Centro Comercial Quicentro, patio de comidas" maxLength={200} className={inputCls(err("meetup"))} />
                    <FieldError msg={err("meetup")} testid="err-meetup" />
                  </div>
                )}
                {shippingConfig && (
                  <div className="mt-3 flex items-start gap-2 text-[11px] text-white/45"><Info size={12} className="mt-0.5 flex-none" />
                    <span>Prellenamos los costos desde tu configuración de envíos. Puedes ajustarlos solo para esta subasta.</span></div>
                )}
              </Section>

              <Section n="04" title="REGLAS DE LA SUBASTA" invalid={!!err("price")} testid="auctions-section-rules">
                <div id="fld-price">
                  <FieldLabel required tipText="Precio desde el que arranca la puja, en dólares enteros (sin centavos). Un precio bajo (ejemplo: $0 o $1) genera más pujas y entusiasmo." tipTestid="tip-starting-price">Precio de salida (USD)</FieldLabel>
                  <input data-testid="auctions-starting-price" type="text" inputMode="numeric" value={form.starting_price}
                         onChange={(e) => upd("starting_price", e.target.value.replace(/[^\d.,]/g, "").replace(",", "."))} placeholder="0 para empezar en cero" className={inputCls(err("price"))} />
                  <FieldError msg={err("price")} testid="err-price" />
                  <p className="mt-2 text-[12px] text-white/50">Un precio inicial bajo atrae más pujas y aumenta la emoción. Es la mejor forma de generar interacción.</p>
                </div>
                <div className="mt-5">
                  {resubmitId && (
                    <div className="mb-3 rounded-[14px] border border-[#34D399]/40 bg-[#34D399]/[.06] p-3 text-[13px] text-white/80" data-testid="resubmit-fee-note">
                      <b className="text-[#34D399]">Ya pagaste esta duración.</b> El pago queda a favor de esta subasta: al reenviarla no se te cobra de nuevo. La duración no se puede cambiar.
                    </div>
                  )}
                  <fieldset disabled={!!resubmitId} className={resubmitId ? "opacity-60" : ""}>
                  <FieldLabel required tipText="Cuánto dura la subasta. La Subasta Turbo (1 día) es gratis. 3, 5 y 7 días tienen un costo pequeño. Ninguna subasta paga comisión." tipTestid="tip-duration">Duración</FieldLabel>
                  <button type="button" onClick={() => upd("duration_days", 1)} data-testid="auctions-duration-1"
                          className={`w-full rounded-[16px] border p-4 flex items-center justify-between gap-3 text-left transition-colors ${Number(form.duration_days) === 1 ? "border-[#34D399] bg-[#34D399]/[.12]" : "border-[#34D399]/40 bg-[#34D399]/[.04] hover:bg-[#34D399]/[.08]"}`}>
                    <span><span className="flex items-center gap-1.5 text-[17px] font-extrabold"><Zap size={16} className="text-[#34D399]" fill="currentColor" /> Subasta Turbo · 1 día</span>
                      <span className="block text-[13px] text-white/60 mt-0.5">No te cuesta NADA · sin comisiones</span></span>
                    <span className="sx-h text-[26px] text-[#34D399]">GRATIS</span>
                  </button>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[3, 5, 7].map((d) => {
                      const sel = Number(form.duration_days) === d;
                      return (
                        <button key={d} type="button" onClick={() => upd("duration_days", d)} data-testid={`auctions-duration-${d}`}
                                className={`rounded-[14px] border py-3 text-center transition-colors ${sel ? "border-[#FF4438]/80 bg-[#FF4438]/[.10]" : "border-white/12 bg-black/20 hover:border-white/30"}`}>
                          <span className="block font-extrabold">{d} días</span>
                          <span className="sx-mono block text-[11px] text-white/50 mt-0.5">${Number(dayPrices[d] || 0).toFixed(2)}</span>
                        </button>
                      );
                    })}
                  </div>
                  </fieldset>
                </div>
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                  <FieldLabel required tipText="Tiempo que tiene el ganador para pagar después de cerrada la subasta. Si no paga en ese plazo, se cae la venta y su cuenta queda baneada." tipTestid="tip-payment-deadline">Plazo de pago del ganador</FieldLabel>
                  <Select value={String(form.payment_deadline_days)} onValueChange={(v) => upd("payment_deadline_days", parseInt(v))}>
                    <SelectTrigger data-testid="auctions-payment-deadline" className="w-auto h-9 border-0 bg-transparent font-extrabold gap-2"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#161616] border-white/15 text-white"><SelectItem value="1">1 día</SelectItem><SelectItem value="2">2 días</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className={`mt-3 rounded-[14px] border px-4 py-3 flex items-center justify-between ${dayFee === 0 ? "border-[#34D399]/35 bg-[#34D399]/[.05]" : "border-white/12 bg-black/20"}`}>
                  <span className="text-[13px] text-white/65 flex items-center gap-1.5"><Clock size={13} /> Costo por duración</span>
                  <span className={`font-extrabold ${dayFee === 0 ? "text-[#34D399]" : "text-white"}`} data-testid="auctions-day-fee">{dayFee === 0 ? "GRATIS · Subasta Turbo" : `$${dayFee.toFixed(2)}`}</span>
                </div>
                <div className="mt-3 text-[12px] text-white/50 leading-relaxed space-y-1.5">
                  {dayFee === 0 ? (
                    <p>La <b className="text-[#34D399]">Subasta Turbo (1 día) no te cuesta NADA</b>. Y HitTCG no cobra comisión en ninguna subasta — el 100% del precio final es para ti.</p>
                  ) : (
                    <>
                      <p><b className="text-white">¿Qué cubre este costo?</b> Mantiene la subasta activa el tiempo extra que elegiste y financia la promoción dentro de la plataforma.</p>
                      <p><b className="text-white">¿Cuándo se cobra?</b> Al enviar la subasta — te redirigimos a la pantalla de pago de Payphone. Recién después del pago confirmado, la subasta entra a revisión del administrador.</p>
                      <p className="text-amber-300"><b>Importante:</b> Solo pagas los días extra. HitTCG no cobra comisión en ninguna subasta — el precio final es íntegro para ti.</p>
                      <p className="text-white/70" data-testid="fee-non-refundable"><b className="text-white">No reembolsable:</b> {FEE_NON_REFUNDABLE}</p>
                    </>
                  )}
                </div>
              </Section>

              {dayFee === 0 ? (
                <div className="rounded-[18px] border border-amber-500/40 bg-amber-500/[.07] p-4 text-[13px] text-white/75 leading-relaxed">
                  <b className="text-amber-300">Revisión por el administrador:</b> Todas las subastas pasan por una revisión manual antes de salir al aire. Verificamos que las fotos sean reales, que el producto no esté prohibido (armas, drogas, cuentas robadas, etc.) y que la descripción sea honesta. Normalmente demora menos de 24 horas.
                </div>
              ) : (
                <div className="rounded-[18px] border border-sky-500/35 bg-sky-500/[.07] p-4 text-[13px] text-white/75 leading-relaxed">
                  <b className="text-sky-300">Flujo de pago primero, revisión después:</b> Al enviar, te llevaremos a la pantalla de pago (Payphone). Una vez confirmado el pago de ${dayFee.toFixed(2)}, la subasta pasa a revisión del administrador (menos de 24 horas) y, al aprobarse, sale al aire.
                </div>
              )}
            </div>
          )}
        </div>

        {step !== 2 && (
          <div className="fixed inset-x-0 bottom-0 z-40 bg-[#080808]/95 backdrop-blur-xl border-t border-white/10" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            <div className="max-w-3xl mx-auto px-5 py-3.5">
              {step === 1 && (
                <button type="button" disabled={!game || (game === "other" && !otherGameKey)} onClick={() => setStep(2)} data-testid="auctions-step1-continue"
                        className="sx-btn w-full h-[54px] text-base flex items-center justify-center gap-2">Continuar <ArrowRight size={17} /></button>
              )}
              {step === 3 && (
                <>
                  <div className="mb-2.5">
                    {terms.accepted ? <TermsAccepted status={terms.status} /> : <TermsCheck checked={termsOk} onChange={setTermsOk} testid="crear-terms-check" />}
                  </div>
                  {MISSING.length > 0 && (
                    <div className="text-[13px] text-[#ff8a80] mb-2.5 flex flex-wrap gap-x-2 gap-y-1" data-testid="auctions-missing">
                      <b>Te faltan {MISSING.length} campo{MISSING.length === 1 ? "" : "s"}:</b>
                      {MISSING.map(([k, l, id], i) => (
                        <span key={k}>{i > 0 && <span className="text-white/30 mr-2">·</span>}
                          <button type="button" className="underline underline-offset-2" onClick={() => { setShowErrors(true); document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" }); }}>{l}</button></span>
                      ))}
                    </div>
                  )}
                  {MISSING.length > 0 ? (
                    <button type="button" onClick={goFirstMissing} data-testid="auctions-submit"
                            className="w-full h-[54px] rounded-full border border-[#FF4438]/50 bg-[#FF4438]/[.14] text-white/85 font-extrabold text-base">Ir al primer campo que falta ↑</button>
                  ) : (
                    <HitButton type="button" onClick={submit} disabled={!canSubmit} loading={submitting} done={publish.done} tone="dark" doneText="Enviado"
                               loadingText={resubmitId || dayFee === 0 ? "Enviando…" : "Publicando…"} data-testid="auctions-submit"
                               className="sx-btn w-full h-[54px] text-base inline-flex items-center justify-center gap-2.5">
                      {resubmitId ? "Reenviar a revisión · sin pagar de nuevo" : dayFee === 0 ? "Enviar a revisión" : `Ir a pagar $${dayFee.toFixed(2)}`}
                    </HitButton>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={leave.open} onOpenChange={(o) => !o && leave.cancel()}>
        <AlertDialogContent data-testid="leave-guard-modal"
          className="sx bg-[#101010] border-white/12 rounded-[28px] p-6 gap-0 max-sm:!top-auto max-sm:!bottom-0 max-sm:!left-0 max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!w-full max-sm:!max-w-none max-sm:!rounded-b-none">
          <div className="sm:hidden mx-auto -mt-2 mb-4 w-11 h-1 rounded-full bg-white/25" />
          <div className="sx-mono text-[10px] tracking-[.14em] text-amber-400">TIENES PROGRESO SIN GUARDAR</div>
          <AlertDialogTitle className="sx-h text-[30px] mt-2">¿Salir <span className="sx-it">sin terminar la subasta?</span></AlertDialogTitle>
          <div className="grid grid-cols-3 gap-1.5 mt-4">{[1, 2, 3].map((n) => <span key={n} className="h-1 rounded-full" style={{ background: step > n ? "#34D399" : step === n ? "rgba(255,68,56,.6)" : "rgba(255,255,255,.12)" }} />)}</div>
          <AlertDialogDescription className="text-white/60 text-sm mt-4 leading-relaxed">
            {editDraftToken
              ? "Perderás los cambios que hiciste en este borrador. El borrador guardado se mantiene."
              : "Perderás todo el progreso: juego, datos y fotos que hayas cargado. Esta subasta aún no se ha guardado."}
          </AlertDialogDescription>
          <div className="mt-6 space-y-2.5">
            <AlertDialogCancel data-testid="leave-guard-stay" className="w-full h-[54px] m-0 rounded-full bg-white text-[#080808] font-extrabold border-0 hover:bg-white/90 hover:text-[#080808]">Seguir creando</AlertDialogCancel>
            <AlertDialogAction data-testid="leave-guard-leave" onClick={leave.confirmLeave} className="w-full h-[52px] rounded-full bg-transparent border border-[#FF4438]/50 text-[#ff6b61] font-extrabold hover:bg-[#FF4438]/10">Salir y perder progreso</AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
